import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  RefreshControl,
  Platform,
  ActivityIndicator,
  FlatList,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useAppStore } from '../store/appStore';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { apiService } from '../services/api';
import { PermissionSummary } from '../constants/permissionLevels';
import { Header, ScreenLayout, ConfirmationModal, PermissionSummaryChip, BusinessUserPicker } from '../components';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface Department {
  _id: string;
  name: string;
  description?: string;
  businessId: string;
  managerId?: string | {
    _id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    username?: string;
  };
  isActive?: boolean;
  memberCount?: number;
  status: number;
  createdAt: string;
  updatedAt: string;
}

export const DepartmentsScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { showNotification, navigateTo, setActiveTab, activeTab } = useAppContext();
  const { currentBusiness } = useAppStore();
  const { t } = useLocale();
  const rtl = useRTLStyles();

  // State
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState<Department | null>(null);
  const [deleting, setDeleting] = useState(false);
  const isLoadingRef = useRef(false);

  // Bulk selection
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [summaries, setSummaries] = useState<Record<string, PermissionSummary>>({});
  const [showBulkMembersPicker, setShowBulkMembersPicker] = useState(false);
  const [bulkMembersSaving, setBulkMembersSaving] = useState(false);
  const [showBulkRemoveModal, setShowBulkRemoveModal] = useState(false);
  const [bulkRemoving, setBulkRemoving] = useState(false);

  // Set active tab
  useEffect(() => {
    setActiveTab('departments');
  }, [setActiveTab]);

  const loadSummaries = async (departmentIds: string[]) => {
    if (!currentBusiness?._id || departmentIds.length === 0) return;
    try {
      const response = await apiService.getBulkDepartmentSummaries(currentBusiness._id, departmentIds);
      if (response.statusCode === 1000 && response.data) {
        setSummaries(response.data);
      }
    } catch (error) {
      console.error('❌ [DepartmentsScreen] Error loading permission summaries:', error);
    }
  };

  // Load departments - NOT memoized to avoid dependency loops
  const loadDepartments = async (isRefresh = false) => {
    if (!currentBusiness?._id) {
      console.log('⚠️ [DepartmentsScreen] No business ID available');
      setLoading(false);
      setRefreshing(false);
      return;
    }

    // Prevent multiple simultaneous loads
    if (loading && !isRefresh) {
      return;
    }

    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      console.log('🔍 [DepartmentsScreen] Loading departments for business:', currentBusiness._id);
      const response = await apiService.getDepartments(currentBusiness._id);

      if (response.statusCode === 1000 && response.data) {
        console.log('✅ [DepartmentsScreen] Departments loaded:', response.data.length);
        setDepartments(response.data || []);
        loadSummaries((response.data || []).map((d: Department) => d._id));
      } else {
        console.error('❌ [DepartmentsScreen] Failed to load departments:', response.errors);
        showNotification('error', 'Error', response.errors?.[0]?.message || 'Failed to load departments');
      }
    } catch (error: any) {
      console.error('❌ [DepartmentsScreen] Error loading departments:', error);
      showNotification('error', 'Error', 'Failed to load departments');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Reload when screen loads or business changes
  useEffect(() => {
    let isMounted = true;

    if (currentBusiness?._id && !isLoadingRef.current) {
      isLoadingRef.current = true;

      const fetchDepartments = async () => {
        if (!isMounted || !currentBusiness?._id) {
          isLoadingRef.current = false;
          setLoading(false);
          return;
        }

        try {
          setLoading(true);
          console.log('🔍 [DepartmentsScreen] Loading departments for business:', currentBusiness._id);
          const response = await apiService.getDepartments(currentBusiness._id);

          if (!isMounted) {
            isLoadingRef.current = false;
            setLoading(false);
            return;
          }

          if (response.statusCode === 1000 && response.data) {
            console.log('✅ [DepartmentsScreen] Departments loaded:', response.data.length);
            setDepartments(response.data || []);
            loadSummaries((response.data || []).map((d: Department) => d._id));
          } else {
            console.error('❌ [DepartmentsScreen] Failed to load departments:', response.errors);
            showNotification('error', 'Error', response.errors?.[0]?.message || 'Failed to load departments');
          }
        } catch (error: any) {
          if (!isMounted) {
            isLoadingRef.current = false;
            setLoading(false);
            return;
          }
          console.error('❌ [DepartmentsScreen] Error loading departments:', error);
          showNotification('error', 'Error', 'Failed to load departments');
        } finally {
          isLoadingRef.current = false;
          if (isMounted) {
            setLoading(false);
          }
        }
      };

      fetchDepartments();
    } else if (!currentBusiness?._id) {
      setLoading(false);
    }

    return () => {
      isMounted = false;
      isLoadingRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBusiness?._id]);

  // Handle delete department
  const handleDelete = async () => {
    if (!departmentToDelete) return;

    try {
      setDeleting(true);
      const response = await apiService.deleteDepartment(departmentToDelete._id);

      if (response.statusCode === 1000) {
        showNotification('success', 'Success', 'Department deleted successfully');
        setShowDeleteModal(false);
        setDepartmentToDelete(null);
        loadDepartments();
      } else {
        showNotification('error', 'Error', response.errors?.[0]?.message || 'Failed to delete department');
      }
    } catch (error: any) {
      console.error('❌ [DepartmentsScreen] Error deleting department:', error);
      showNotification('error', 'Error', 'Failed to delete department');
    } finally {
      setDeleting(false);
    }
  };

  // Handle create department - navigate to add screen
  const handleCreateDepartment = () => {
    navigateTo('department-add');
  };

  // Handle edit department — always the full AddDepartmentScreen (mode="edit"), on
  // every platform including web. This used to branch on web into a separate,
  // thinner right-pane form (name/description/active-toggle only) that had no
  // manager field at all — the one place a manager should be changeable was
  // unreachable from the departments list on web. AddDepartmentScreen is the single
  // reusable form for both create and edit now; no second implementation to drift
  // out of sync with it.
  const handleEdit = (department: Department) => {
    if (selectionMode) {
      toggleSelect(department._id);
      return;
    }
    navigateTo('department-edit', department._id);
  };

  // Handle tab press
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const getStatusColor = (isActive: boolean) => {
    return isActive ? colors.success : colors.error;
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === departments.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(departments.map(d => d._id)));
    }
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  const handleBulkManageMembers = () => {
    setShowBulkMembersPicker(true);
  };

  const handleBulkMembersConfirm = async (userIds: string[]) => {
    if (!currentBusiness?._id || userIds.length === 0) {
      setShowBulkMembersPicker(false);
      return;
    }
    try {
      setBulkMembersSaving(true);
      let totalAdded = 0;
      for (const departmentId of selectedIds) {
        const response = await apiService.bulkAddUsersToDepartment(departmentId, userIds);
        if (response.statusCode === 1000 && response.data) {
          totalAdded += response.data.added || 0;
        }
      }
      showNotification('success', 'Success', t('departments.bulk.membersAddSuccess', { count: totalAdded }));
      setShowBulkMembersPicker(false);
      exitSelectionMode();
      loadDepartments();
    } catch (error: any) {
      console.error('❌ [DepartmentsScreen] Error bulk-adding members:', error);
      showNotification('error', 'Error', 'Failed to add members');
    } finally {
      setBulkMembersSaving(false);
    }
  };

  const handleBulkSetPermissions = () => {
    navigateTo('business-permissions', undefined, {
      targetType: 'department',
      targetIds: Array.from(selectedIds),
    });
  };

  const handleBulkRemoveRequest = () => {
    setShowBulkRemoveModal(true);
  };

  const confirmBulkRemove = async () => {
    try {
      setBulkRemoving(true);
      let removed = 0;
      for (const departmentId of selectedIds) {
        const response = await apiService.deleteDepartment(departmentId);
        if (response.statusCode === 1000) removed++;
      }
      showNotification('success', 'Success', `${removed} department(s) removed`);
      setShowBulkRemoveModal(false);
      exitSelectionMode();
      loadDepartments();
    } catch (error: any) {
      console.error('❌ [DepartmentsScreen] Error bulk-removing departments:', error);
      showNotification('error', 'Error', 'Failed to remove departments');
    } finally {
      setBulkRemoving(false);
    }
  };

  const renderDepartmentCard = ({ item }: { item: Department }) => {
    const statusColor = getStatusColor(item.isActive || false);
    const isSelected = selectedIds.has(item._id);
    return (
      <TouchableOpacity
        style={[styles.departmentCard, { flexDirection: rtl.rowDirection }]}
        onPress={() => handleEdit(item)}
        activeOpacity={0.7}
      >
        {selectionMode && (
          <View style={styles.checkboxWrapper}>
            <View style={[styles.checkbox, isSelected && styles.checkboxChecked]}>
              {isSelected && <Ionicons name="checkmark" size={14} color={colors.white} />}
            </View>
          </View>
        )}
        <View style={styles.departmentCardContent}>
          <View style={styles.departmentInfo}>
            <Text style={[styles.departmentName, { textAlign: rtl.textStart }]}>{item.name}</Text>
            {item.description && (
              <Text style={[styles.departmentDescription, { textAlign: rtl.textStart }]}>{item.description}</Text>
            )}
            <View style={[styles.departmentMeta, { flexDirection: rtl.rowDirection }]}>
              <View style={[styles.statusBadge, { backgroundColor: String(statusColor) + '20' }]}>
                <Text style={[styles.statusText, { color: statusColor }]}>
                  {item.isActive ? 'Active' : 'Inactive'}
                </Text>
              </View>
              <View style={[styles.memberCountBadge, { flexDirection: rtl.rowDirection }]}>
                <Ionicons name="people-outline" size={12} color={colors.text.secondary} />
                <Text style={styles.metaText}>{t('departments.memberCount', { count: item.memberCount || 0 })}</Text>
              </View>
              {item.managerId && (
                <Text style={styles.metaText}>
                  • Manager: {
                    typeof item.managerId === 'object' && item.managerId
                      ? `${item.managerId.firstName || ''} ${item.managerId.lastName || ''}`.trim() || item.managerId.email || 'Assigned'
                      : 'Assigned'
                  }
                </Text>
              )}
            </View>
            <PermissionSummaryChip
              targetType="department"
              targetId={item._id}
              summary={summaries[item._id]}
              onPress={() => navigateTo('business-permissions', undefined, { targetType: 'department', targetId: item._id })}
            />
          </View>
        </View>
        {!selectionMode && (
          <View style={[styles.departmentActions, { flexDirection: rtl.rowDirection }]}>
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => handleEdit(item)}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons name="create-outline" size={20} color={colors.primary} />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => {
                setDepartmentToDelete(item);
                setShowDeleteModal(true);
              }}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons name="trash-outline" size={20} color={colors.error} />
            </TouchableOpacity>
          </View>
        )}
      </TouchableOpacity>
    );
  };

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
    >
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <View style={[styles.headerContent, { flexDirection: rtl.rowDirection }]}>
            <View style={styles.headerLeft}>
              <Text style={styles.headerTitle}>Departments</Text>
              {!loading && (
                <Text style={styles.headerSubtitle}>
                  {selectionMode
                    ? t('departments.bulk.selectedCount', { count: selectedIds.size })
                    : `${departments.length} ${departments.length === 1 ? 'department' : 'departments'}`}
                </Text>
              )}
            </View>

            <View style={[styles.headerActions, { flexDirection: rtl.rowDirection }]}>
              {selectionMode ? (
                <TouchableOpacity style={styles.textButton} onPress={exitSelectionMode}>
                  <Text style={styles.textButtonLabel}>{t('departments.bulk.exitSelection')}</Text>
                </TouchableOpacity>
              ) : (
                <>
                  <TouchableOpacity
                    style={styles.iconButton}
                    onPress={() => setSelectionMode(true)}
                    disabled={departments.length === 0}
                  >
                    <Ionicons name="checkbox-outline" size={22} color={colors.primary} />
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.addButton}
                    onPress={handleCreateDepartment}
                    activeOpacity={0.8}
                  >
                    <Ionicons name="add" size={24} color={colors.white} />
                  </TouchableOpacity>
                </>
              )}
            </View>
          </View>
        </View>

        {/* Bulk action bar */}
        {selectionMode && (
          <View style={[styles.bulkActionBar, { flexDirection: rtl.rowDirection }]}>
            <TouchableOpacity style={styles.bulkActionButton} onPress={handleSelectAll}>
              <Ionicons name="checkmark-done-outline" size={16} color={colors.primary} />
              <Text style={styles.bulkActionText}>{t('departments.bulk.selectAll')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.bulkActionButton}
              onPress={handleBulkManageMembers}
              disabled={selectedIds.size === 0}
            >
              <Ionicons name="person-add-outline" size={16} color={selectedIds.size === 0 ? colors.text.disabled : colors.primary} />
              <Text style={[styles.bulkActionText, selectedIds.size === 0 && styles.bulkActionTextDisabled]}>{t('departments.bulk.manageMembers')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.bulkActionButton}
              onPress={handleBulkSetPermissions}
              disabled={selectedIds.size === 0}
            >
              <Ionicons name="shield-checkmark-outline" size={16} color={selectedIds.size === 0 ? colors.text.disabled : colors.primary} />
              <Text style={[styles.bulkActionText, selectedIds.size === 0 && styles.bulkActionTextDisabled]}>{t('departments.bulk.setPermissions')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.bulkActionButton}
              onPress={handleBulkRemoveRequest}
              disabled={selectedIds.size === 0}
            >
              <Ionicons name="trash-outline" size={16} color={selectedIds.size === 0 ? colors.text.disabled : colors.error} />
              <Text style={[styles.bulkActionText, { color: colors.error }, selectedIds.size === 0 && styles.bulkActionTextDisabled]}>{t('userSetup.bulk.remove')}</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Content */}
        {loading && !refreshing ? (
          <View style={styles.centerContainer}>
            <ActivityIndicator size="large" color={colors.primary} />
            <Text style={styles.loadingText}>Loading departments...</Text>
          </View>
        ) : departments.length === 0 ? (
          <View style={styles.centerContainer}>
            <Ionicons name="business-outline" size={64} color={colors.text.secondary} />
            <Text style={styles.emptyText}>No departments yet</Text>
            <Text style={styles.emptySubtext}>Create your first department to organize your team</Text>
            <TouchableOpacity
              style={styles.createButton}
              onPress={handleCreateDepartment}
            >
              <Ionicons name="add" size={20} color={colors.white} />
              <Text style={styles.createButtonText}>Create Department</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <FlatList
            data={departments}
            renderItem={renderDepartmentCard}
            keyExtractor={(item) => item._id}
            contentContainerStyle={styles.listContent}
            refreshControl={
              <RefreshControl refreshing={refreshing} onRefresh={() => loadDepartments(true)} />
            }
            showsVerticalScrollIndicator={false}
          />
        )}
      </View>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        visible={showDeleteModal}
        title="Delete Department"
        message={`Are you sure you want to delete "${departmentToDelete?.name}"? This will remove all users from this department.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onClose={() => {
          setShowDeleteModal(false);
          setDepartmentToDelete(null);
        }}
        loading={deleting}
      />

      {/* Bulk remove confirmation */}
      <ConfirmationModal
        visible={showBulkRemoveModal}
        title={t('departments.bulk.selectAll')}
        message={`Are you sure you want to delete ${selectedIds.size} department(s)? This will remove all users from them.`}
        confirmText={t('userSetup.bulk.remove')}
        cancelText={t('permissions.cancel')}
        onConfirm={confirmBulkRemove}
        onClose={() => setShowBulkRemoveModal(false)}
        loading={bulkRemoving}
        type="error"
      />

      {/* Bulk manage members picker */}
      <BusinessUserPicker
        visible={showBulkMembersPicker}
        onClose={() => setShowBulkMembersPicker(false)}
        onConfirm={handleBulkMembersConfirm}
        mode="multiple"
        source="users"
        title={t('departments.bulk.manageMembers')}
      />
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  header: {
    backgroundColor: colors.background.surface,
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    ...designTokens.shadows.sm,
  },
  headerContent: {
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerLeft: {
    flex: 1,
  },
  headerTitle: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: '700',
    color: colors.textDark,
    fontFamily: typography.fontFamily.bold,
    marginBottom: spacing.xs,
  },
  headerSubtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    fontFamily: typography.fontFamily.regular,
  },
  headerActions: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  iconButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  textButton: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  textButtonLabel: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
    color: colors.primary,
  },
  addButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    ...designTokens.shadows.md,
  },
  bulkActionBar: {
    backgroundColor: colors.background.surface,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    gap: spacing.lg,
  },
  bulkActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  bulkActionText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
  },
  bulkActionTextDisabled: {
    color: colors.text.disabled,
  },
  listContent: {
    padding: spacing.lg,
    paddingBottom: 100,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: spacing.xl * 2,
  },
  loadingText: {
    marginTop: spacing.md,
    color: colors.text.secondary,
    fontSize: typography.fontSize.base,
  },
  emptyText: {
    marginTop: spacing.md,
    fontSize: 18,
    fontWeight: '600',
    color: colors.text.primary,
  },
  emptySubtext: {
    marginTop: spacing.sm,
    color: colors.text.secondary,
    textAlign: 'center',
    paddingHorizontal: spacing.xl,
  },
  createButton: {
    marginTop: spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderRadius: 8,
  },
  createButtonText: {
    marginLeft: spacing.sm,
    color: colors.white,
    fontWeight: '600',
  },
  departmentCard: {
    backgroundColor: colors.background.surface,
    borderRadius: 12,
    marginBottom: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
    alignItems: 'center',
    ...designTokens.shadows.sm,
  },
  checkboxWrapper: {
    paddingLeft: spacing.md,
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  departmentCardContent: {
    flex: 1,
    padding: spacing.md,
  },
  departmentInfo: {
    flex: 1,
    gap: spacing.xs,
  },
  departmentActions: {
    alignItems: 'center',
    paddingRight: spacing.sm,
    gap: spacing.xs,
  },
  actionButton: {
    padding: spacing.sm,
    borderRadius: 8,
  },
  departmentName: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.text.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  departmentDescription: {
    fontSize: typography.fontSize.base,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.regular,
  },
  departmentMeta: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  memberCountBadge: {
    alignItems: 'center',
    gap: spacing.xs / 2,
  },
  statusBadge: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs / 2,
    borderRadius: 12,
  },
  statusText: {
    fontSize: typography.fontSize.xs,
    fontWeight: '600',
    fontFamily: typography.fontFamily.semibold,
  },
  metaText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.regular,
  },
});
